interface DownloadInfo {
  platform: string;
  displayName?: string;
  url: string;
  arch?: string;
  filename?: string;
  version?: string;
}

interface DownloadStats {
  total: number;
  byPlatform: Record<string, number>;
  lastUpdated: string;
}

export class App {
  private downloadLinks: Record<string, DownloadInfo> = {};
  private downloadStats: DownloadStats | null = null;

  constructor() {
    // Initialize app
  }

  async init() {
    console.log('Ash homepage initialized');
    this.setupBackgroundVideo();
    this.createDownloadCounter(); // Create counter element first
    await this.loadDownloadLinks();
    await this.loadDownloadStats();
    this.updateDownloadCounter(); // Update after stats are loaded
    this.setupEventListeners();
  }

  setupBackgroundVideo() {
    const video = document.getElementById('background-video') as HTMLVideoElement;
    if (video) {
      // Ensure video plays
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(err => {
          console.log('Video autoplay prevented:', err);
        });
      });
      
      // Restart video if it ends (safety net, loop should handle this)
      video.addEventListener('ended', () => {
        video.currentTime = 0;
        video.play().catch(err => {
          console.log('Video restart failed:', err);
        });
      });
      
      // Pause video on mobile to save bandwidth (optional)
      if (window.innerWidth < 768) {
        video.pause();
        video.style.display = 'none';
      }
    }
  }

  private readonly downloadLinksCacheKey = 'ash-download-links';

  loadCachedDownloadLinks() {
    try {
      const raw = localStorage.getItem(this.downloadLinksCacheKey);
      if (!raw) {
        return;
      }
      const data = JSON.parse(raw);
      if (!data || typeof data !== 'object') {
        return;
      }
      this.downloadLinks = data;
      this.updateDownloadButtons();
    } catch (error) {
      console.error('Error reading cached download links:', error);
    }
  }

  storeDownloadLinks(data: any) {
    let updated = false;
    Object.values(data).forEach((info: any) => {
      if (!info?.platform || !info?.url || info.error) {
        return;
      }
      this.downloadLinks[info.platform] = info as DownloadInfo;
      updated = true;
    });

    if (!updated) {
      return;
    }

    try {
      localStorage.setItem(this.downloadLinksCacheKey, JSON.stringify(this.downloadLinks));
    } catch (error) {
      console.error('Error saving cached download links:', error);
    }
    this.updateDownloadButtons();
  }

  async loadDownloadLinks() {
    // Show the last known links immediately, then fill in each OS as its CDN metadata arrives.
    this.loadCachedDownloadLinks();

    try {
      const response = await fetch('/api/downloads');
      if (!response.ok) {
        throw new Error('Failed to fetch download links');
      }
      this.storeDownloadLinks(await response.json());

      const refreshing = (response.headers.get('X-Download-Refreshing') || '')
        .split(',')
        .map(group => group.trim())
        .filter(Boolean);

      await Promise.all(refreshing.map(async (group) => {
        const freshResponse = await fetch(`/api/downloads?group=${encodeURIComponent(group)}&wait=1`);
        if (freshResponse.ok) {
          this.storeDownloadLinks(await freshResponse.json());
        }
      }));
    } catch (error) {
      console.error('Error loading download links:', error);
    }
  }

  updateDownloadButtons() {
    const downloadButtons = document.querySelectorAll('.download-btn');
    downloadButtons.forEach(button => {
      const platform = button.getAttribute('data-platform');
      if (platform && this.downloadLinks[platform]) {
        const downloadInfo = this.downloadLinks[platform];
        // Set href to the download URL
        button.setAttribute('href', downloadInfo.url);
        // Set download attribute to force download instead of navigation
        if (downloadInfo.filename) {
          button.setAttribute('download', downloadInfo.filename);
        }
        const buttonElement = button as HTMLElement;
        buttonElement.style.opacity = '';
        buttonElement.style.cursor = '';
      } else {
        // Disable button if no download link available
        button.setAttribute('href', '#');
        const buttonElement = button as HTMLElement;
        buttonElement.style.opacity = '0.5';
        buttonElement.style.cursor = 'not-allowed';
      }
    });
  }

  setupEventListeners() {
    const downloadButtons = document.querySelectorAll('.download-btn');
    downloadButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        const platform = button.getAttribute('data-platform');
        
        if (!platform) {
          e.preventDefault();
          return;
        }
        
        // Check if download link is available
        if (!this.downloadLinks[platform]) {
          console.error(`Download link not found for platform: ${platform}`);
          e.preventDefault();
          alert('Download link is not available. Please try again later.');
          return;
        }
        
        const downloadInfo = this.downloadLinks[platform];
        const downloadUrl = downloadInfo.url;
        
        console.log(`Downloading for ${platform}: ${downloadUrl}`);
        
        // Track download count
        try {
          const trackResponse = await fetch(`/api/download/${platform}/track`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          if (trackResponse.ok) {
            // Reload stats after tracking
            await this.loadDownloadStats();
            this.updateDownloadCounter();
          }
        } catch (error) {
          console.error('Error tracking download:', error);
          // Continue with download even if tracking fails
        }
        
        // For cross-origin downloads, we need to handle it differently
        // If the download attribute doesn't work (CORS issue), 
        // we can programmatically create a link and click it
        if (downloadUrl.startsWith('http')) {
          try {
            // Try to trigger download programmatically for better control
            const link = document.createElement('a');
            link.href = downloadUrl;
            if (downloadInfo.filename) {
              link.download = downloadInfo.filename;
            }
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Prevent default to avoid double navigation
            e.preventDefault();
            
            console.log(`Download triggered: ${downloadInfo.filename || downloadUrl}`);
          } catch (error) {
            console.error('Error triggering download:', error);
            // If programmatic download fails, let the default href behavior work
          }
        }
      });
    });
  }

  async loadDownloadStats() {
    try {
      const response = await fetch('/api/downloads/stats');
      if (response.ok) {
        this.downloadStats = await response.json();
      }
    } catch (error) {
      console.error('Error loading download stats:', error);
    }
  }

  createDownloadCounter() {
    // Create counter element
    const counter = document.createElement('div');
    counter.id = 'download-counter';
    counter.className = 'download-counter';
    document.body.appendChild(counter);
    this.updateDownloadCounter();
  }

  updateDownloadCounter() {
    const counter = document.getElementById('download-counter');
    if (!counter) return;

    const total = this.downloadStats?.total || 0;
    const formatted = total.toLocaleString();
    counter.textContent = formatted;
    counter.style.display = 'block';
  }

}

